import React from 'react'
import { Outlet, useNavigate, useParams } from 'react-router-dom'

import { useApolloClient } from '@apollo/client'
import { Box } from '@factorialco/deprecated-design-system'
import { OneDataCollection, OneEmptyState } from '@factorialco/f0-react/dist/experimental'
import { Plus, Upload } from '@factorialco/f0-react/icons/app'
import Envelope from '@factorialco/f0-react/icons/app/Envelope'
import Settings from '@factorialco/f0-react/icons/app/Settings'
import {
  BookkeepersManagementIncidenceDocument,
  CompensationLegalEntitiesDocument,
  EmployeeUpdatesDataCollectionDocument,
  EmployeeUpdatesDataCollectionQuery,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import type { DefaultParams } from 'modules/bookkeepers/hooks/useIncidenceFilters'
import { IncidenceFilterProvider } from 'modules/bookkeepers/lib/IncidenceFilterContext'
import { MessageFilterProvider } from 'modules/bookkeepers/lib/MessageFilterContext'
import { FACTORIAL_APP } from 'modules/bookkeepers/lib/amplitudeData'
import { useBookkeeperView } from 'modules/bookkeepers/lib/bookkeeperViewContext'
import { translatedIncidenceName } from 'modules/bookkeepers/lib/dynamicTranslations'
import { SORT_ATTRIBUTES } from 'modules/bookkeepers/lib/tableSortColumns'
import { getStatusConfig } from 'modules/bookkeepers/lib/tagColor'
import { getUpdateTypeFilterOptions } from 'modules/compensations/lib/updateTypeFilterOptions'
import { useWorkplaceFilter } from 'modules/employees/lib/filters/workplace'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import useBulkIncidenceStatusChange from 'modules/payroll_bundle/hooks/useBulkIncidenceStatusChange'
import { useInhousePayroll } from 'modules/payroll_bundle/hooks/useInhousePayroll'
import {
  INCIDENCE_STATUSES,
  INHOUSE_INCIDENCE_STATUSES,
  inhouseStatusCopies,
  professionalsStatusCopies,
} from 'modules/payroll_bundle/lib/Incidences/Status'
import { PolicyPeriodsProvider } from 'modules/payroll_bundle/lib/policyPeriodsContext'
import NewManualIncidenceModal from 'modules/payroll_bundle/pages/IncidencesPage/components/_ManualIncidenceModal/_New'
// eslint-disable-next-line factorial/no-allowed-mobx-collections
import policyPeriodCollection from 'modules/payroll_bundle/store/collections/policyPeriods'
import PayrollExportModal from 'modules/payroll_exporter/components/PayrollExportModal'

import {
  createBulkActionHandler,
  createOnBulkActionHandler,
  createOnSelectItemsHandler,
  getBulkActionsConfig,
} from './EmployeeUpdatesBulkActions'
import EmployeeUpdatesDataCollectionContent from './EmployeeUpdatesDataCollectionContent'
import {
  DataCollectionState,
  IncidenceItem,
  createDynamicPresets,
  createStateChangeHandler,
  getConnectionPageInfo,
  getStatusCount,
} from './utils'

const MESSAGE_FROM = {
  ADMIN: 'admin',
  BOOKKEEPER: 'bookkeeper',
} as const

const DEFAULT_PER_PAGE = 50

const hasUnreadMessage = (incidence: IncidenceItem, isBookkeeperView: boolean) =>
  incidence.hasMessage &&
  incidence.messageFrom === (isBookkeeperView ? MESSAGE_FROM.ADMIN : MESSAGE_FROM.BOOKKEEPER)

const getMessageFilterVariables = (
  hasMessageValues: (boolean | string)[],
  isBookkeeperView: boolean
): { containsMessage?: boolean; messageFrom?: string } => {
  const hasUnread = hasMessageValues.includes(true)
  const hasRead = hasMessageValues.includes(false)

  if ((hasUnread && hasRead) || (!hasUnread && !hasRead)) return {}
  if (hasUnread && !hasRead) {
    return {
      containsMessage: true,
      messageFrom: isBookkeeperView ? MESSAGE_FROM.ADMIN : MESSAGE_FROM.BOOKKEEPER,
    }
  }
  return { containsMessage: true }
}

const filterReadMessages = (
  nodes: IncidenceItem[],
  hasUnread: boolean,
  hasRead: boolean,
  isBookkeeperView: boolean
): IncidenceItem[] => {
  if (hasRead && !hasUnread) {
    return nodes.filter((incidence) => !hasUnreadMessage(incidence, isBookkeeperView))
  }
  return nodes
}

const EmployeeUpdatesDataCollectionPage = () => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const { id } = useParams()
  const incidenceDetail = <Outlet />
  const openedIncidenceId = id ?? null
  const { hasInhousePayroll } = useInhousePayroll()
  const hasProfessionals = !hasInhousePayroll
  const isBookkeeperView = useBookkeeperView()
  const workplaceFilter = useWorkplaceFilter()

  const {
    company: { id: companyId },
  } = useCurrentSession()

  const client = useApolloClient()

  const [currentDataCollectionState, setCurrentDataCollectionState] =
    React.useState<DataCollectionState>({})
  const visibleItemIdsRef = React.useRef<Set<string>>(new Set())
  const clearSelectedItemsRef = React.useRef<(() => void) | null>(null)
  const selectedItemsMapRef = React.useRef<Map<string, IncidenceItem>>(new Map())

  const buildQueryVariables = React.useCallback(() => {
    const state = currentDataCollectionState
    const filters = state.filters || {}
    const status = filters.status?.filter(Boolean)
    const defaultStatus =
      status && status.length > 0
        ? status
        : hasProfessionals
          ? [INCIDENCE_STATUSES.in_preparation]
          : undefined
    const type = filters.type?.filter(Boolean)
    const legalEntityIds = filters.legalEntityIds?.filter(Boolean)
    const workplaceIds = Array.isArray(filters.workplaceIds)
      ? filters.workplaceIds.filter(Boolean)
      : undefined
    const flattenedType = type?.flat()

    const effectiveDateFrom =
      filters.effectiveDate &&
      typeof filters.effectiveDate === 'object' &&
      'from' in filters.effectiveDate &&
      filters.effectiveDate.from instanceof Date
        ? filters.effectiveDate.from.toISOString().split('T')[0]
        : undefined

    const effectiveDateTo =
      filters.effectiveDate &&
      typeof filters.effectiveDate === 'object' &&
      'to' in filters.effectiveDate &&
      filters.effectiveDate.to instanceof Date
        ? filters.effectiveDate.to.toISOString().split('T')[0]
        : undefined

    return {
      ...(defaultStatus && defaultStatus.length > 0 && { status: defaultStatus }),
      ...(flattenedType?.length && { type: flattenedType }),
      ...(legalEntityIds?.length && { legalEntitiesIds: legalEntityIds }),
      ...(workplaceIds?.length && { workplaceIds }),
      ...(effectiveDateFrom && { startsOn: effectiveDateFrom }),
      ...(effectiveDateTo && { endsOn: effectiveDateTo }),
      ...(state.search && { search: state.search }),
      ...(state.sortings?.[0]?.field && { sortBy: state.sortings[0].field }),
      ...(state.sortings?.[0]?.order && { direction: state.sortings[0].order }),
      ...getMessageFilterVariables(filters.hasMessage ?? [], isBookkeeperView),
      perPage: DEFAULT_PER_PAGE,
    }
  }, [hasProfessionals, isBookkeeperView, currentDataCollectionState])

  const { bulkChangeIncidenceStatuses } = useBulkIncidenceStatusChange({
    onStatusChanged: async () => {
      await client.refetchQueries({
        include: [EmployeeUpdatesDataCollectionDocument],
      })
    },
  })

  const { data, loading } = useQuery(EmployeeUpdatesDataCollectionDocument, {
    variables: {
      perPage: DEFAULT_PER_PAGE,
    },
    fetchPolicy: 'network-only',
    skip: !companyId,
  })

  const incidencesDataFromCollection = React.useMemo<IncidenceItem[]>(() => {
    const nodes = data?.bookkeepersManagement?.incidencesConnection?.nodes ?? []
    return nodes.filter((node): node is IncidenceItem => node !== null && node !== undefined)
  }, [data])

  const shouldShowEnvelopeColumn = React.useMemo(
    () =>
      (data?.bookkeepersManagement?.incidencesConnection?.nodes ?? []).some(
        (incidence: IncidenceItem) => hasUnreadMessage(incidence, isBookkeeperView)
      ),
    [data, isBookkeeperView]
  )

  const [exportModalOpen, setExportModalOpen] = React.useState<boolean>(false)
  const [createIncidenceModalOpen, setCreateIncidenceModalOpen] = React.useState<boolean>(false)

  const handleBulkAction = React.useMemo(
    () =>
      createBulkActionHandler({
        hasInhousePayroll,
        isBookkeeperView,
        bulkChangeIncidenceStatuses,
        filterVisibleItems: (selectedItems) => selectedItems,
      }),
    [hasInhousePayroll, isBookkeeperView, bulkChangeIncidenceStatuses]
  )

  const onSelectItemsHandler = React.useCallback(
    createOnSelectItemsHandler<IncidenceItem>({
      visibleItemIdsRef,
      selectedItemsMapRef,
      clearSelectedItemsRef,
    }),
    []
  )

  const onBulkActionHandler = React.useCallback(
    createOnBulkActionHandler<IncidenceItem>({
      visibleItemIdsRef,
      selectedItemsMapRef,
      clearSelectedItemsRef,
      handleBulkAction,
    }),
    [handleBulkAction]
  )

  const defaultIncidencesFilterValues: DefaultParams = {
    filters: {
      status: hasInhousePayroll
        ? [INHOUSE_INCIDENCE_STATUSES.to_do, INHOUSE_INCIDENCE_STATUSES.doing].join(',')
        : [INCIDENCE_STATUSES.in_preparation].join(','),
    },
    direction: 'asc',
    attribute: SORT_ATTRIBUTES.incidence_effective_date,
  }

  const defaultMessagesFilterValues: DefaultParams = {
    direction: 'desc',
    containsMessage: true,
    attribute: SORT_ATTRIBUTES.incidence_effective_date,
  }

  const handleExportClick = () => {
    track(trackingEvents.compensations.openEmployeeUpdatesExportModal, {
      application: FACTORIAL_APP,
    })
    setExportModalOpen(true)
  }

  const handleCloseCreateIncidenceModal = () => {
    setCreateIncidenceModalOpen(false)
  }

  const handleCreateIncidenceSuccess = async () => {
    setCreateIncidenceModalOpen(false)
    const queryVariables = buildQueryVariables()

    await Promise.all([
      client.query({
        query: EmployeeUpdatesDataCollectionDocument,
        variables: queryVariables,
        fetchPolicy: 'network-only',
      }),
      client.refetchQueries({
        include: [BookkeepersManagementIncidenceDocument],
      }),
    ])
  }

  const getStatusOptions = () => {
    if (hasInhousePayroll) {
      return Object.entries(INHOUSE_INCIDENCE_STATUSES).map(([_key, value]) => ({
        label: inhouseStatusCopies()[value],
        value,
      }))
    } else {
      const statusOrder = [
        INCIDENCE_STATUSES.in_preparation,
        INCIDENCE_STATUSES.to_do,
        INCIDENCE_STATUSES.doing,
        INCIDENCE_STATUSES.done,
        INCIDENCE_STATUSES.discarded,
      ]
      const statusCopies = professionalsStatusCopies()
      return statusOrder.map((status) => ({
        label: statusCopies[status],
        value: status,
      }))
    }
  }

  const statusCountsData = React.useMemo(() => {
    return {
      bookkeepersManagement: data?.bookkeepersManagement,
    }
  }, [data])

  const getStatusCountCallback = React.useCallback(
    (status: string) => getStatusCount(status, statusCountsData),
    [statusCountsData]
  )

  const dynamicPresets = React.useMemo(
    () => createDynamicPresets(statusCountsData, getStatusCountCallback, hasInhousePayroll),
    [statusCountsData, getStatusCountCallback, hasInhousePayroll]
  )

  const hasMessageFilterRef = React.useRef<{ hasUnread: boolean; hasRead: boolean }>({
    hasUnread: false,
    hasRead: false,
  })
  const isFirstLoadRef = React.useRef<boolean>(true)

  const stateChangeHandler = React.useMemo(() => createStateChangeHandler(() => {}), [])

  const [filtersKey, setFiltersKey] = React.useState<string>('')

  const handleStateChange = React.useCallback(
    (newState: DataCollectionState) => {
      const previousSearch = currentDataCollectionState.search || ''
      const nextSearch = newState.search || ''

      if (previousSearch !== nextSearch) {
        clearSelectedItemsRef.current?.()
        selectedItemsMapRef.current = new Map()
      }

      setCurrentDataCollectionState(newState)
      const hasMessageValues = newState.filters?.hasMessage ?? []
      hasMessageFilterRef.current = {
        hasUnread: hasMessageValues.includes(true),
        hasRead: hasMessageValues.includes(false),
      }
      setFiltersKey(JSON.stringify({ filters: newState.filters, search: newState.search }))
      stateChangeHandler(newState)
    },
    [stateChangeHandler, currentDataCollectionState.search]
  )

  React.useEffect(() => {
    if (filtersKey) {
      clearSelectedItemsRef.current?.()
      selectedItemsMapRef.current = new Map()
    }
  }, [filtersKey])

  const source = useGraphqlDataCollectionSource({
    document: EmployeeUpdatesDataCollectionDocument,
    fetchPolicy: 'network-only',
    paginationType: 'pages',
    mapData: (queryData: EmployeeUpdatesDataCollectionQuery) => {
      const nodes = queryData?.bookkeepersManagement?.incidencesConnection?.nodes ?? []
      const { hasUnread, hasRead } = hasMessageFilterRef.current
      const filteredNodes = filterReadMessages(nodes, hasUnread, hasRead, isBookkeeperView)
      const newVisibleItemIds = new Set(filteredNodes.map((item) => item.id))

      visibleItemIdsRef.current = newVisibleItemIds
      return filteredNodes
    },
    mapVariables: ({ filters, sortings, search }) => {
      const isFirstLoad = isFirstLoadRef.current
      if (isFirstLoad) {
        isFirstLoadRef.current = false
      }
      const status = filters?.status?.filter(Boolean)
      const defaultStatus =
        status && status.length > 0
          ? status
          : isFirstLoad
            ? hasInhousePayroll
              ? [INHOUSE_INCIDENCE_STATUSES.to_do, INHOUSE_INCIDENCE_STATUSES.doing]
              : [INCIDENCE_STATUSES.in_preparation]
            : undefined
      const type = filters?.type?.filter(Boolean)
      const legalEntityIds = filters?.legalEntityIds?.filter(Boolean)
      const workplaceIds = Array.isArray(filters?.workplaceIds)
        ? filters.workplaceIds.filter(Boolean)
        : undefined
      const flattenedType = type?.flat()

      const effectiveDateFrom =
        filters?.effectiveDate &&
        typeof filters.effectiveDate === 'object' &&
        'from' in filters.effectiveDate &&
        filters.effectiveDate.from instanceof Date
          ? filters.effectiveDate.from.toISOString().split('T')[0]
          : undefined

      const effectiveDateTo =
        filters?.effectiveDate &&
        typeof filters.effectiveDate === 'object' &&
        'to' in filters.effectiveDate &&
        filters.effectiveDate.to instanceof Date
          ? filters.effectiveDate.to.toISOString().split('T')[0]
          : undefined

      return {
        ...(defaultStatus && defaultStatus.length > 0 && { status: defaultStatus }),
        ...(flattenedType?.length && { type: flattenedType }),
        ...(legalEntityIds?.length && { legalEntitiesIds: legalEntityIds }),
        ...(workplaceIds?.length && { workplaceIds }),
        ...(effectiveDateFrom && { startsOn: effectiveDateFrom }),
        ...(effectiveDateTo && { endsOn: effectiveDateTo }),
        ...(search && { search }),
        ...(sortings?.[0]?.field && { sortBy: sortings[0].field }),
        ...(sortings?.[0]?.order && { direction: sortings[0].order }),
        ...getMessageFilterVariables(filters?.hasMessage ?? [], isBookkeeperView),
      }
    },
    mapPagination: (queryData: EmployeeUpdatesDataCollectionQuery) => {
      const pagination = getConnectionPageInfo(
        queryData?.bookkeepersManagement?.incidencesConnection
      )

      const hasActiveSearch = Boolean(currentDataCollectionState.search)

      let total = pagination.totalCount
      if (hasActiveSearch) {
        const nodes = queryData?.bookkeepersManagement?.incidencesConnection?.nodes ?? []
        const { hasUnread, hasRead } = hasMessageFilterRef.current
        const filteredNodes = filterReadMessages(nodes, hasUnread, hasRead, isBookkeeperView)
        total = filteredNodes.length
      }

      return {
        total,
        cursor: pagination.endCursor,
        hasNextPage: pagination.hasNextPage,
      }
    },
    itemUrl: (item: IncidenceItem) =>
      navigation.compensations.employeeUpdates.id.fullPath({ id: item.id }),
    presets: dynamicPresets,
    currentFilters: {
      status: hasInhousePayroll
        ? [INHOUSE_INCIDENCE_STATUSES.to_do, INHOUSE_INCIDENCE_STATUSES.doing]
        : [INCIDENCE_STATUSES.in_preparation],
    },
    filters: {
      type: {
        label: i18n.t('compensations.employee_updates_data_collection_page.filters.update_type'),
        type: 'in',
        options: {
          options: getUpdateTypeFilterOptions(),
        },
      },
      status: {
        label: i18n.t('compensations.employee_updates_data_collection_page.filters.status'),
        type: 'in',
        options: {
          options: getStatusOptions(),
        },
      },
      effectiveDate: {
        label: i18n.t('compensations.employee_updates_data_collection_page.filters.effective_date'),
        type: 'date',
        options: {
          mode: 'range',
        },
      },
      ...(hasProfessionals && {
        hasMessage: {
          label: i18n.t('compensations.employee_updates_data_collection_page.filters.messages'),
          type: 'in',
          options: {
            options: [
              {
                label: i18n.t(
                  'compensations.employee_updates_data_collection_page.filters.messages_options.read'
                ),
                value: false,
              },
              {
                label: i18n.t(
                  'compensations.employee_updates_data_collection_page.filters.messages_options.unread'
                ),
                value: true,
              },
            ],
          },
        },
      }),
      legalEntityIds: {
        label: i18n.t('compensations.employee_updates_data_collection_page.filters.legal_entity'),
        type: 'in',
        options: {
          cache: true,
          options: async () => {
            const result = await client.query({
              query: CompensationLegalEntitiesDocument,
              variables: {
                companyId,
              },
              fetchPolicy: 'cache-first',
            })

            return (
              result.data?.apiCore?.company?.legalEntities?.map((legalEntity) => ({
                label: legalEntity.legalName || '',
                value: String(legalEntity.id),
              })) || []
            )
          },
        },
      },
      workplaceIds: {
        ...workplaceFilter,
        label: i18n.t('compensations.employee_updates_data_collection_page.filters.workplace'),
      },
    },
    defaultSortings: {
      field: 'incidence_effective_date',
      order: 'desc',
    },
    search: {
      enabled: true,
    },
    perPage: DEFAULT_PER_PAGE,
    primaryActions: () => ({
      label: i18n.t('compensations.employee_updates_data_collection_page.filters.export'),
      icon: Upload,
      onClick: handleExportClick,
    }),
    secondaryActions: {
      expanded: 1,
      actions: () => [
        {
          label: i18n.t('compensations.employee_updates_data_collection_page.filters.create'),
          icon: Plus,
          onClick: () => setCreateIncidenceModalOpen(true),
        },
      ],
    },
    selectable: (item) => {
      const hasActiveSearch = Boolean(currentDataCollectionState.search)
      if (hasActiveSearch) {
        return visibleItemIdsRef.current.has(item.id) ? item.id : undefined
      }
      return item.id
    },
    bulkActions: () => getBulkActionsConfig(hasInhousePayroll),
  })

  const totalCount = data?.bookkeepersManagement?.incidencesConnection?.totalCount ?? 0
  const hasNoData = !loading && totalCount === 0

  return (
    <ResponsivePage.Body>
      <IncidenceFilterProvider defaultFilterValues={defaultIncidencesFilterValues}>
        <MessageFilterProvider defaultFilterValues={defaultMessagesFilterValues}>
          <EmployeeUpdatesDataCollectionContent
            incidencesData={incidencesDataFromCollection}
            dataCollectionTable={
              <Box paddingY="s16" width="full">
                {hasNoData ? (
                  <Box height="full" minHeight="s400" justifyContent="center" alignItems="center">
                    <OneEmptyState
                      title={i18n.t(
                        'compensations.employee_updates_data_collection_page.empty_state.title'
                      )}
                      description={i18n.t(
                        'compensations.employee_updates_data_collection_page.empty_state.description'
                      )}
                      emoji="📄"
                      actions={[
                        {
                          label: i18n.t(
                            'compensations.employee_updates_data_collection_page.empty_state.button'
                          ),
                          icon: Settings,
                          onClick: () =>
                            navigate(navigation.settings.compensations.employeeUpdates.fullPath()),
                          type: 'default',
                          variant: 'outline',
                        },
                      ]}
                    />
                  </Box>
                ) : (
                  <OneDataCollection
                    key={`employee-updates-${hasInhousePayroll ? 'inhouse' : 'professionals'}`}
                    fullHeight
                    source={source}
                    onSelectItems={onSelectItemsHandler}
                    onStateChange={(newState) => {
                      const statusFilter = newState.filters?.status
                      const typeFilter = newState.filters?.type
                      const legalEntityFilter = newState.filters?.legalEntityIds
                      const hasMessageFilter = newState.filters?.hasMessage
                      const effectiveDateFilter = newState.filters?.effectiveDate
                      const workplaceIdsFilter = newState.filters?.workplaceIds

                      handleStateChange({
                        filters: {
                          status: Array.isArray(statusFilter)
                            ? statusFilter.map((status: unknown) => String(status))
                            : undefined,
                          type: Array.isArray(typeFilter)
                            ? typeFilter.map((type: unknown) => String(type))
                            : undefined,
                          legalEntityIds: Array.isArray(legalEntityFilter)
                            ? legalEntityFilter.map((legalEntityId: unknown) =>
                                String(legalEntityId)
                              )
                            : undefined,
                          workplaceIds: Array.isArray(workplaceIdsFilter)
                            ? workplaceIdsFilter.map((workplaceId: unknown) => String(workplaceId))
                            : undefined,
                          effectiveDate:
                            effectiveDateFilter &&
                            typeof effectiveDateFilter === 'object' &&
                            'from' in effectiveDateFilter
                              ? {
                                  from: effectiveDateFilter.from || undefined,
                                  to:
                                    'to' in effectiveDateFilter
                                      ? effectiveDateFilter.to || undefined
                                      : undefined,
                                }
                              : undefined,
                          hasMessage: Array.isArray(hasMessageFilter)
                            ? hasMessageFilter.map((hasMessage: unknown) => Boolean(hasMessage))
                            : undefined,
                        },
                        search: newState.search,
                        sortings: Array.isArray(newState.sortings)
                          ? newState.sortings.map(
                              (sorting: { field?: string; order?: string }) => ({
                                field: sorting.field,
                                order: sorting.order,
                              })
                            )
                          : newState.sortings &&
                              typeof newState.sortings === 'object' &&
                              'field' in newState.sortings
                            ? [
                                {
                                  field:
                                    'field' in newState.sortings
                                      ? newState.sortings.field
                                      : undefined,
                                  order:
                                    'order' in newState.sortings
                                      ? newState.sortings.order
                                      : undefined,
                                },
                              ]
                            : undefined,
                      })
                    }}
                    onBulkAction={onBulkActionHandler}
                    storage={{
                      features: ['filters', 'sortings'],
                    }}
                    visualizations={[
                      {
                        type: 'table',
                        options: {
                          columns: [
                            {
                              label: i18n.t(
                                'compensations.employee_updates_data_collection_page.columns.employee'
                              ),
                              render: (item: IncidenceItem) => {
                                const firstName = item.employee?.firstName ?? ''
                                const lastName = item.employee?.lastName ?? ''
                                const name = [firstName, lastName].filter(Boolean).join(' ')
                                return {
                                  type: 'person',
                                  value: {
                                    firstName,
                                    lastName,
                                    name,
                                    src: '',
                                  },
                                }
                              },
                              sorting: 'employee_first_name',
                            },
                            {
                              label: i18n.t(
                                'compensations.employee_updates_data_collection_page.columns.type'
                              ),
                              render: (item: IncidenceItem) => ({
                                type: 'text',
                                value: translatedIncidenceName(
                                  item.name ?? '',
                                  item.customName ?? ''
                                ),
                              }),
                              sorting: 'incidence_type',
                            },
                            {
                              label: i18n.t(
                                'compensations.employee_updates_data_collection_page.columns.status'
                              ),
                              render: (item: IncidenceItem) => {
                                const statusConfig = getStatusConfig(item.status, hasInhousePayroll)
                                return {
                                  type: 'status',
                                  value: {
                                    status: statusConfig.type,
                                    label: statusConfig.label,
                                  },
                                }
                              },
                              sorting: 'incidence_status',
                            },
                            {
                              label: i18n.t(
                                'compensations.employee_updates_data_collection_page.columns.effective_date'
                              ),
                              render: (item: IncidenceItem) => ({
                                type: 'date',
                                value: item.startsOn ? new Date(item.startsOn) : undefined,
                              }),
                              sorting: 'incidence_effective_date',
                            },
                            // @ts-expect-error - OneDataCollection supports 'tag' type but it's not in the TypeScript definitions
                            ...(shouldShowEnvelopeColumn
                              ? [
                                  {
                                    label: '',
                                    render: (item: IncidenceItem) =>
                                      hasUnreadMessage(item, isBookkeeperView)
                                        ? { type: 'tag', value: { icon: Envelope } }
                                        : '',
                                  },
                                ]
                              : []),
                          ],
                          frozenColumns: 1,
                        },
                      },
                    ]}
                  />
                )}
                {exportModalOpen && (
                  <PolicyPeriodsProvider policyPeriodCollection={policyPeriodCollection}>
                    <PayrollExportModal
                      onClose={() => setExportModalOpen(false)}
                      selectedCompany={null}
                      isProfessionals={false}
                    />
                  </PolicyPeriodsProvider>
                )}
                {createIncidenceModalOpen && (
                  <NewManualIncidenceModal
                    onClose={handleCloseCreateIncidenceModal}
                    onSuccess={handleCreateIncidenceSuccess}
                    hasProfessionals={hasProfessionals}
                  />
                )}
              </Box>
            }
            incidenceDetail={incidenceDetail}
            openedIncidenceId={openedIncidenceId}
          />
        </MessageFilterProvider>
      </IncidenceFilterProvider>
    </ResponsivePage.Body>
  )
}

export default EmployeeUpdatesDataCollectionPage
